import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, cur as curFmt, fmtDate } from "@/components/common";
import { BankAccountPicker } from "@/components/BankAccountsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, CheckCircle, PaperPlaneTilt, PencilSimple, Plus, Trash, LockSimple, ClockCounterClockwise, Coins, Bank, FilePdf } from "@phosphor-icons/react";
import { drawPurchaseOrder } from "@/lib/poPdf";
import { drawCompanyBlock, companyLines } from "@/lib/companyHeader";

const METHODS = { bank_transfer: "Bank transfer", cash: "Cash", cheque: "Cheque", card: "Card", other: "Other" };

function AuditTrail({ entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="hp-card p-6" data-testid="audit-trail">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3 flex items-center gap-2">
        <ClockCounterClockwise size={16} className="text-primary" /> Edit history
      </p>
      <ul className="space-y-2">
        {[...entries].reverse().map((a, i) => (
          <li key={i} className="text-sm flex items-center justify-between gap-3">
            <span>{a.action}</span>
            <span className="text-muted-foreground text-xs">{a.by} · {new Date(a.ts).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isTeam } = useAuth();
  const [inv, setInv] = useState(null);
  const money = (n) => curFmt(n, inv?.currency || "USD");
  const [billOpen, setBillOpen] = useState(false);
  const [billCur, setBillCur] = useState("USD");
  const [billBank, setBillBank] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const openBilling = async () => { try { const { data } = await api.get("/settings/bank-accounts"); setBankAccounts(data); setBillCur(inv.currency || "USD"); setBillBank(inv.bank?.id || (data.find((a) => a.is_default) || data[0])?.id || ""); setBillOpen(true); } catch (e) { toast.error("Could not load bank accounts"); } };
  const saveBilling = async () => { try { const { data } = await api.patch(`/invoices/${id}/billing`, { currency: billCur, bank_account_id: billBank || null }); setInv((p) => ({ ...p, ...data })); setBillOpen(false); toast.success("Invoice currency / bank account updated"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); } };
  const [bank, setBank] = useState(null);
  const [company, setCompany] = useState(null);
  const bankOrDefault = () => inv?.bank || bank || { bank_name: "Abu Dhabi Islamic Bank (ADIB)", branch: "Sheikh Zayed, UAE", account_type: "USD - Savings Account", account_no: "29345468", iban: "AE600500000000029345468", swift: "ABDIAEADXXX" };

  const load = useCallback(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => toast.error("Invoice not found"));
  }, [id]);
  useEffect(load, [load]);
  useEffect(() => { api.get("/settings/bank").then((r) => setBank(r.data)).catch(() => {}); api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);

  const markPaid = async () => {
    try {
      await api.patch(`/invoices/${id}/paid`);
      toast.success("Marked as paid");
      load();
    } catch { toast.error("Failed"); }
  };

  const [pay, setPay] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const savePayment = async () => {
    if (!(Number(pay.amount) > 0)) return toast.error("Enter an amount");
    setPayBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success(data.status === "paid" ? "Payment recorded — invoice fully paid" : `Payment recorded · balance due ${money(data.balance_due)}`);
      setPay(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record payment"); } finally { setPayBusy(false); }
  };
  const removePayment = async (p) => {
    if (!window.confirm(`Remove payment of ${money(p.amount)}?`)) return;
    try { await api.delete(`/invoices/${id}/payments/${p.id}`); toast.success("Payment removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove payment"); }
  };

  const [emailing, setEmailing] = useState(false);
  const sendEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/invoices/${id}/email`);
      toast.success(`Invoice emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email");
    } finally {
      setEmailing(false);
    }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [ed, setEd] = useState(null);
  const emptyItem = () => ({ name: "", sku: "", quantity: 1, unit_price: 0 });
  const openEdit = () => {
    setEd({
      items: (inv.items || []).map((it) => ({ name: it.name, sku: it.sku || "", quantity: it.quantity, unit_price: it.unit_price })),
      shipping_charge: inv.shipping_charge || 0,
      tax_rate: inv.tax_rate || 0,
      due_date: inv.due_date ? inv.due_date.slice(0, 10) : "",
      notes: inv.notes || "",
      patient_name: inv.patient_name || "",
      postal_code: inv.postal_code || "",
      destination_country: inv.destination_country || "",
      cep: inv.cep || "",
      cpf: inv.cpf || "",
      address: inv.address || "",
    });
    setEditOpen(true);
  };
  const edItem = (i, f, v) => setEd((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, [f]: v } : it)) }));
  const edSubtotal = ed ? ed.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0) : 0;
  const saveEdit = async () => {
    const valid = ed.items.filter((it) => it.name.trim());
    if (valid.length === 0) return toast.error("Add at least one line item");
    try {
      await api.patch(`/invoices/${id}`, {
        items: valid.map((it) => ({ name: it.name, sku: it.sku, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 })),
        shipping_charge: Number(ed.shipping_charge) || 0,
        tax_rate: Number(ed.tax_rate) || 0,
        due_date: ed.due_date || null,
        notes: ed.notes,
        patient_name: ed.patient_name || null,
        postal_code: ed.postal_code || null,
        destination_country: ed.destination_country || null,
        cep: ed.cep || null,
        cpf: ed.cpf || null,
        address: ed.address || null,
      });
      toast.success("Invoice updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const isConsol = (inv?.order_numbers?.length || 0) > 1 || inv?.consolidated;
  const bd = bankOrDefault();

  const downloadPDF = async (withPo = false) => {
    const doc = new jsPDF();
    const blue = [30, 127, 212];
    const gray = [130, 130, 130];
    const line = [225, 228, 232];
    try {
      const dataUrl = await fetch("/heliopolis-logo.png").then((b) => b.blob()).then((b) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); }));
      doc.addImage(dataUrl, "PNG", 20, 14, 20, 20);
    } catch (e) { /* logo optional */ }
    doc.setTextColor(...gray); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    const sepY = drawCompanyBlock(doc, company, gray);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...gray);
    doc.text("INVOICE", 190, 18, { align: "right" });
    doc.setTextColor(20, 20, 20); doc.setFontSize(17);
    doc.text(String(inv.invoice_number), 190, 27, { align: "right" });
    doc.setFontSize(8); doc.setTextColor(...blue);
    doc.text(String(inv.status || "").toUpperCase(), 190, 34, { align: "right" });
    doc.setDrawColor(...line); doc.setLineWidth(0.4); doc.line(20, sepY, 190, sepY);
    let y = sepY + 9;
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
    doc.text("BILL TO", 20, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(20, 20, 20);
    doc.text(String(inv.client_name || ""), 20, y + 7);
    doc.setFontSize(9); doc.setTextColor(...gray);
    doc.text(isConsol ? `Orders: ${(inv.order_numbers || []).join(", ")}` : `Order: ${inv.order_number}`, 20, y + 13);
    if (inv.client_reference) doc.text(`Reference: ${String(inv.client_reference).slice(0, 44)}`, 20, y + 19);
    doc.text(`Issued ${fmtDate(inv.issued_date)}`, 190, y, { align: "right" });
    doc.text(`Due ${fmtDate(inv.due_date)}`, 190, y + 6, { align: "right" });
    doc.setTextColor(20, 20, 20);
    y += 22;

    y += 10;
    doc.setFont("helvetica", "bold");
    if (isConsol) { doc.text("Order", 20, y); doc.text("Product", 48, y); } else { doc.text("Product", 20, y); }
    doc.text("Qty", 120, y);
    doc.text("Unit", 145, y);
    doc.text("Amount", 175, y);
    doc.line(20, y + 2, 195, y + 2);
    doc.setFont("helvetica", "normal");
    y += 8;
    (Array.isArray(inv.items) ? inv.items : []).forEach((it) => {
      if (isConsol) { doc.text(String(it.order_number || "—"), 20, y); doc.text(String(it.name).slice(0, 34), 48, y); }
      else { doc.text(String(it.name).slice(0, 50), 20, y); }
      doc.text(String(it.quantity), 120, y);
      doc.text(money(it.unit_price), 145, y);
      doc.text(money(it.quantity * it.unit_price), 175, y);
      y += 7;
    });
    doc.line(20, y, 195, y);
    y += 8;
    doc.text("Subtotal", 145, y); doc.text(money(inv.subtotal), 175, y);
    if (inv.shipping_charge > 0) { y += 7; doc.text("Shipping", 145, y); doc.text(money(inv.shipping_charge), 175, y); }
    if (inv.handling_total > 0) { y += 7; doc.text("Cool box handling", 145, y); doc.text(money(inv.handling_total), 175, y); }
    y += 7;
    doc.text(`Tax (${inv.tax_rate}%)`, 145, y); doc.text(money(inv.tax), 175, y);
    if (inv.transfer_fee > 0) { y += 7; doc.text("Warehouse transfer fee", 145, y); doc.text(money(inv.transfer_fee), 175, y); }
    y += 7;
    doc.setFont("helvetica", "bold");
    doc.text("Total", 145, y); doc.text(money(inv.total), 175, y);

    const pts = inv.patients || [];
    if (pts.length) {
      y += 14; doc.setDrawColor(...line); doc.line(20, y - 6, 190, y - 6);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text(`PATIENTS / DESTINATIONS (${pts.length})`, 20, y);
      doc.setFont("helvetica", "normal");
      pts.forEach((p) => {
        y += 9;
        doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(20, 20, 20);
        const nm = String(p.patient_name || "—");
        doc.text(nm.slice(0, 46), 20, y);
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
        doc.text(String(p.order_number || ""), 190, y, { align: "right" });
        y += 5; doc.text(([p.destination_country, p.postal_code, p.cep, p.cpf, p.address].filter(Boolean).join(" · ") || "—").slice(0, 95), 20, y);
      });
    } else if (inv.patient_name || inv.destination_country || inv.address || inv.postal_code || inv.cpf) {
      y += 12; doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...gray);
      doc.text("PATIENT / DESTINATION", 20, y);
      doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20); doc.setFontSize(9);
      [inv.patient_name && `Patient: ${inv.patient_name}`, inv.destination_country && `Destination: ${inv.destination_country}`, inv.postal_code && `Postal code: ${inv.postal_code}`, inv.cep && `CEP: ${inv.cep}`, inv.cpf && `CPF: ${inv.cpf}`, inv.address && `Address: ${inv.address}`].filter(Boolean).forEach((l) => { y += 6; doc.text(String(l).slice(0, 90), 20, y); });
    }

    const b = bankOrDefault();
    y += 16; doc.setDrawColor(...line); doc.line(20, y - 6, 190, y - 6);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...blue);
    doc.text(`PAYMENT — BANK DETAILS (${(inv.currency || b.currency || "USD").toUpperCase()} ACCOUNT)`, 20, y);
    doc.setFont("helvetica", "normal");
    const cell = (lab, val, x, yy) => { doc.setTextColor(...gray); doc.setFontSize(7.5); doc.text(lab, x, yy); doc.setTextColor(20, 20, 20); doc.setFontSize(9.5); doc.text(String(val || ""), x, yy + 5); };
    y += 9;
    cell("BANK", b.bank_name, 20, y); cell("BRANCH", b.branch, 85, y); cell("ACCOUNT TYPE", b.account_type, 145, y);
    y += 14;
    cell("ACCOUNT NO", b.account_no, 20, y); cell("IBAN", b.iban, 85, y); cell("SWIFT", b.swift, 145, y);
    y += 14;
    cell("REFERENCE", inv.invoice_number, 20, y);

    if (withPo) {
      const oids = inv.order_ids?.length ? inv.order_ids : inv.order_id ? [inv.order_id] : [];
      let pos = [];
      try { pos = (await api.get("/purchase-orders")).data.filter((p) => oids.includes(p.order_id)); } catch (e) { toast.error("Could not load purchase orders"); return; }
      if (pos.length === 0) { toast.error("No purchase order is linked to this invoice's order"); return; }
      pos.forEach((po) => { doc.addPage(); drawPurchaseOrder(doc, po); });
      doc.save(`${inv.invoice_number}+PO.pdf`);
      return;
    }
    doc.save(`${inv.invoice_number}.pdf`);
  };

  if (!inv) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate("/invoices")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-invoices">
        <ArrowLeft size={16} /> Invoices
      </button>

      <div className="hp-card p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-border pb-6">
          <div>
            <img src="/heliopolis-logo.png" alt="Heliopolis" className="h-12 sm:h-16 mb-3" />
            <div className="text-xs text-muted-foreground space-y-0.5" data-testid="invoice-company-block">{companyLines(company).map((l, i) => <p key={i} className={i === 0 && company ? "font-semibold text-foreground" : "break-words"}>{l}</p>)}</div>
          </div>
          <div className="sm:text-right">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Invoice</p>
            <p className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight break-all">{inv.invoice_number}</p>
            <div className="mt-2 flex sm:justify-end"><StatusBadge status={inv.status} /></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 py-6 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Bill To</p>
            <p className="font-medium mt-1 break-words">{inv.client_name}</p>
            <p className="text-muted-foreground" data-testid="invoice-source-orders">{inv.order_numbers?.length > 1 ? `Orders: ${inv.order_numbers.join(", ")}` : `Order ${inv.order_number}`}</p>
            {inv.exchange_rate && <p className="text-xs text-muted-foreground" data-testid="invoice-fx-note">Converted from USD at 1 USD = {inv.exchange_rate} {inv.currency}{inv.base_total != null ? ` · base USD ${Number(inv.base_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""}</p>}
            {inv.client_reference && <p className="text-muted-foreground" data-testid="invoice-reference">Ref: {inv.client_reference}</p>}
          </div>
          <div className="sm:text-right">
            <p className="text-muted-foreground">Issued {fmtDate(inv.issued_date)}</p>
            <p className="text-muted-foreground">Due {fmtDate(inv.due_date)}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-y border-border">
              {isConsol && <th className="py-2 font-semibold">Order</th>}
              <th className="py-2 font-semibold">Product</th>
              <th className="py-2 font-semibold text-right">Qty</th>
              <th className="py-2 font-semibold text-right">Unit</th>
              <th className="py-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(inv.items) ? inv.items : []).map((it, i) => (
              <tr key={i} className="border-b border-border/60">
                {isConsol && <td className="py-2 text-muted-foreground text-xs" data-testid={`invoice-line-order-${i}`}>{it.order_number || "—"}</td>}
                <td className="py-2 font-medium">{it.name}</td>
                <td className="py-2 text-right">{it.quantity}</td>
                <td className="py-2 text-right">{money(it.unit_price)}</td>
                <td className="py-2 text-right">{money(it.quantity * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <div className="flex justify-end mt-6">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="invoice-subtotal">{money(inv.subtotal)}</span></div>
            {inv.shipping_charge > 0 && <div className="flex justify-between" data-testid="invoice-shipping-row"><span className="text-muted-foreground">Shipping</span><span data-testid="invoice-shipping">{money(inv.shipping_charge)}</span></div>}
            {inv.handling_total > 0 && <div className="flex justify-between" data-testid="invoice-handling-row"><span className="text-muted-foreground">Cool box handling</span><span data-testid="invoice-handling">{money(inv.handling_total)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({inv.tax_rate}%)</span><span data-testid="invoice-tax">{money(inv.tax)}</span></div>
            {inv.transfer_fee > 0 && <div className="flex justify-between" data-testid="invoice-transfer-fee-row"><span className="text-muted-foreground">Warehouse transfer fee{inv.transfer_fee_note ? ` · ${inv.transfer_fee_note}` : ""}</span><span data-testid="invoice-transfer-fee">{money(inv.transfer_fee)}</span></div>}
            <div className="flex justify-between border-t border-border pt-2 font-heading font-extrabold text-lg"><span>Total</span><span data-testid="invoice-total">{money(inv.total)}</span></div>
            {inv.amount_paid > 0 && (<>
              <div className="flex justify-between text-emerald-700" data-testid="invoice-paid-row"><span>Paid to date</span><span data-testid="invoice-amount-paid">− {money(inv.amount_paid)}</span></div>
              <div className={`flex justify-between border-t border-border pt-2 font-heading font-extrabold ${inv.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`}><span>Balance due</span><span data-testid="invoice-balance-due">{money(inv.balance_due)}</span></div>
            </>)}
          </div>
        </div>

        {inv.payments?.length > 0 && (
          <div className="mt-8 border-t border-border pt-5" data-testid="invoice-payments">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Payments received ({inv.payments.length})</p>
            <div className="space-y-2">
              {inv.payments.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-border px-3 py-2 text-sm" data-testid={`invoice-payment-${p.id}`}>
                  <div><span className="font-semibold tabular-nums">{money(p.amount)}</span> <span className="text-muted-foreground text-xs">· {fmtDate(p.payment_date)} · {METHODS[p.method] || p.method}{p.reference ? ` · Ref ${p.reference}` : ""}{p.notes ? ` · ${p.notes}` : ""} · {p.recorded_by}</span></div>
                  {isTeam && <button type="button" onClick={() => removePayment(p)} className="text-muted-foreground hover:text-destructive" title="Remove payment" data-testid={`invoice-payment-del-${p.id}`}><Trash size={15} /></button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {inv.patients?.length > 0 && (
          <div className="mt-8 border-t border-border pt-5" data-testid="invoice-ptn-list">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Patients / Destinations ({inv.patients.length})</p>
            <div className="space-y-3">
              {inv.patients.map((p, i) => (
                <div key={i} className="rounded-sm border border-border p-3 text-sm" data-testid={`invoice-ptn-item-${i}`}>
                  <p className="font-semibold">{p.patient_name || "—"} <span className="text-xs font-normal text-muted-foreground">· {p.order_number}</span></p>
                  <p className="text-xs text-muted-foreground mt-1">{[p.destination_country, p.postal_code, p.cep, p.cpf, p.address].filter(Boolean).join(" · ") || "No destination details"}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {!inv.patients?.length && (inv.patient_name || inv.postal_code || inv.address || inv.destination_country || inv.cpf || inv.cep) && (
          <div className="mt-8 border-t border-border pt-5" data-testid="invoice-ptn">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Patient / Destination</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              {inv.patient_name && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Patient</p><p className="font-medium mt-0.5">{inv.patient_name}</p></div>}
              {inv.destination_country && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Destination</p><p className="font-medium mt-0.5">{inv.destination_country}</p></div>}
              {inv.postal_code && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Postal code</p><p className="font-medium mt-0.5">{inv.postal_code}</p></div>}
              {inv.cep && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">CEP</p><p className="font-medium mt-0.5">{inv.cep}</p></div>}
              {inv.cpf && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">CPF</p><p className="font-medium mt-0.5">{inv.cpf}</p></div>}
              {inv.address && <div className="col-span-2 sm:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Address</p><p className="font-medium mt-0.5">{inv.address}</p></div>}
            </div>
          </div>
        )}

        <div className="mt-8 border-t border-border pt-5" data-testid="invoice-bank-details">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">Payment — Bank Details ({(inv.currency || bd.currency || "USD").toUpperCase()} Account)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm [&_p.font-medium]:break-all">
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Bank</p><p className="font-medium mt-0.5">{bd.bank_name}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Branch</p><p className="font-medium mt-0.5">{bd.branch}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Account Type</p><p className="font-medium mt-0.5">{bd.account_type}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Account No</p><p className="font-medium mt-0.5">{bd.account_no}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">IBAN</p><p className="font-medium mt-0.5">{bd.iban}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">SWIFT</p><p className="font-medium mt-0.5">{bd.swift}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Reference</p><p className="font-medium mt-0.5" data-testid="invoice-payment-reference">{inv.invoice_number}</p></div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 page-actions">
        <Button className="rounded-sm" onClick={() => downloadPDF(false)} data-testid="download-invoice-pdf">
          <DownloadSimple size={16} className="mr-2" /> Download PDF
        </Button>
        {isTeam && (
          <Button variant="outline" className="rounded-sm" onClick={() => downloadPDF(true)} title="One PDF: invoice first, then the linked purchase order(s)" data-testid="download-invoice-po-pdf">
            <FilePdf size={16} className="mr-2" /> Invoice + PO (one PDF)
          </Button>
        )}
        {isTeam && (
          <Button variant="outline" className="rounded-sm" onClick={sendEmail} disabled={emailing} data-testid="email-invoice-btn">
            <PaperPlaneTilt size={16} className="mr-2" /> {emailing ? "Sending…" : "Send by Email"}
          </Button>
        )}
        {isTeam && inv.status !== "paid" && (
          <Button variant="outline" className="rounded-sm" onClick={openEdit} data-testid="edit-invoice-btn">
            <PencilSimple size={16} className="mr-2" /> Edit Invoice
          </Button>
        )}
        {isTeam && (
          <Button variant="outline" className="rounded-sm" onClick={openBilling} data-testid="change-bank-btn">
            <Bank size={16} className="mr-2" /> Bank / Currency
          </Button>
        )}
        {isTeam && inv.status === "paid" && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="invoice-locked-note">
            <LockSimple size={14} /> Paid &amp; locked
          </span>
        )}
        {isTeam && inv.client_no_ptn && inv.status !== "paid" && inv.status !== "cancelled" && (
          <Button variant="outline" className="rounded-sm" onClick={() => { setPay({ amount: "", payment_date: new Date().toISOString().slice(0, 10), method: "bank_transfer", reference: "", notes: "" }); }} data-testid="record-payment-btn">
            <Coins size={16} className="mr-2" /> Record Payment
          </Button>
        )}
        {isTeam && inv.status !== "paid" && (
          <Button variant="outline" className="rounded-sm" onClick={markPaid} data-testid="mark-invoice-paid">
            <CheckCircle size={16} className="mr-2" /> Mark as Paid
          </Button>
        )}
      </div>

      <AuditTrail entries={inv.audit} />

      <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Record payment — {inv.invoice_number}</DialogTitle></DialogHeader>
          {pay && (
            <div className="space-y-3">
              <div className="rounded-sm bg-muted/40 border border-border px-3 py-2 text-sm flex justify-between"><span className="text-muted-foreground">Balance due</span><span className="font-semibold tabular-nums" data-testid="payment-balance">{money(inv.balance_due ?? inv.total)}</span></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wide">Amount (USD)</Label><Input type="number" min="0" step="0.01" className="mt-1.5 rounded-sm" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Date</Label><Input type="date" className="mt-1.5 rounded-sm" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} data-testid="payment-date" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Method</Label><select className="mt-1.5 h-10 w-full rounded-sm border border-input bg-background px-2 text-sm" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="payment-method">{Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div><Label className="text-xs uppercase tracking-wide">Reference</Label><Input className="mt-1.5 rounded-sm" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="Swift / TT ref" data-testid="payment-ref" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wide">Notes</Label><Input className="mt-1.5 rounded-sm" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} placeholder="e.g. 50% down payment" data-testid="payment-notes" /></div>
              <div className="flex gap-2">
                {[25, 50, 100].map((pct) => <button key={pct} type="button" onClick={() => setPay({ ...pay, amount: ((inv.balance_due ?? inv.total) * pct / 100).toFixed(2) })} className="text-xs rounded-sm border border-border px-2 py-1 hover:bg-muted" data-testid={`payment-pct-${pct}`}>{pct === 100 ? "Full balance" : `${pct}%`}</button>)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPay(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={savePayment} disabled={payBusy} data-testid="payment-save"><Coins size={16} className="mr-2" /> {payBusy ? "Saving…" : "Record payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={billOpen} onOpenChange={setBillOpen}>
        <DialogContent data-testid="billing-dialog">
          <DialogHeader><DialogTitle>Bank account & currency — {inv.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <BankAccountPicker currency={billCur} setCurrency={setBillCur} accountId={billBank} setAccountId={setBillBank} accounts={bankAccounts} />
            <p className="text-xs text-muted-foreground">Changes the currency label and the bank details printed on this invoice (PDF, screen and email). Amounts are not converted.</p>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={saveBilling} data-testid="billing-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {ed && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Invoice {inv.invoice_number}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="border border-border rounded-md overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                  <div className="col-span-5">Item</div><div className="col-span-2">SKU</div><div className="col-span-2">Qty</div><div className="col-span-2">Unit</div><div className="col-span-1" />
                </div>
                {ed.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                    <Input className="col-span-5 rounded-sm h-9" value={it.name} onChange={(e) => edItem(i, "name", e.target.value)} data-testid={`edit-inv-item-name-${i}`} />
                    <Input className="col-span-2 rounded-sm h-9" value={it.sku} onChange={(e) => edItem(i, "sku", e.target.value)} />
                    <Input type="number" className="col-span-2 rounded-sm h-9" value={it.quantity} onChange={(e) => edItem(i, "quantity", e.target.value)} data-testid={`edit-inv-item-qty-${i}`} />
                    <Input type="number" step="0.01" className="col-span-2 rounded-sm h-9" value={it.unit_price} onChange={(e) => edItem(i, "unit_price", e.target.value)} data-testid={`edit-inv-item-price-${i}`} />
                    <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => setEd((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }))} disabled={ed.items.length === 1}><Trash size={16} /></button>
                  </div>
                ))}
                <div className="px-3 py-2 border-t border-border">
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setEd((p) => ({ ...p, items: [...p.items, emptyItem()] }))} data-testid="edit-inv-add-item"><Plus size={14} className="mr-1" /> Add line</Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase tracking-wide">Shipping ($)</Label><Input type="number" step="0.01" className="mt-1.5 rounded-sm" value={ed.shipping_charge} onChange={(e) => setEd({ ...ed, shipping_charge: e.target.value })} data-testid="edit-inv-shipping" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Tax (%)</Label><Input type="number" step="0.1" className="mt-1.5 rounded-sm" value={ed.tax_rate} onChange={(e) => setEd({ ...ed, tax_rate: e.target.value })} data-testid="edit-inv-tax" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Due date</Label><Input type="date" className="mt-1.5 rounded-sm" value={ed.due_date} onChange={(e) => setEd({ ...ed, due_date: e.target.value })} data-testid="edit-inv-due" /></div>
              </div>

              <div className="border-t border-border pt-3">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Patient / destination</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wide">Patient name</Label><Input className="mt-1.5 rounded-sm" value={ed.patient_name} onChange={(e) => setEd({ ...ed, patient_name: e.target.value })} data-testid="edit-inv-patient" /></div>
                  <div><Label className="text-xs uppercase tracking-wide">Destination</Label><Input className="mt-1.5 rounded-sm" value={ed.destination_country} onChange={(e) => setEd({ ...ed, destination_country: e.target.value })} data-testid="edit-inv-dest" /></div>
                  <div><Label className="text-xs uppercase tracking-wide">Postal code</Label><Input className="mt-1.5 rounded-sm" value={ed.postal_code} onChange={(e) => setEd({ ...ed, postal_code: e.target.value })} /></div>
                  {ed.destination_country.trim().toLowerCase() === "brazil" && <div><Label className="text-xs uppercase tracking-wide">CEP</Label><Input className="mt-1.5 rounded-sm" value={ed.cep} onChange={(e) => setEd({ ...ed, cep: e.target.value })} data-testid="edit-inv-cep" /></div>}
                  <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">Address</Label><Input className="mt-1.5 rounded-sm" value={ed.address} onChange={(e) => setEd({ ...ed, address: e.target.value })} /></div>
                </div>
              </div>

              <div className="flex justify-between items-center border-t border-border pt-3">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">New subtotal</span>
                <span className="font-heading text-xl font-extrabold" data-testid="edit-inv-subtotal">{money(edSubtotal)}</span>
              </div>
            </div>
            <DialogFooter><Button className="rounded-sm" onClick={saveEdit} data-testid="save-invoice-edit">Save changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
